import {
    useCallback,
    useEffect,
    useState,
  } from "react";
  
  import {
    getSocket,
  } from "../lib/socket";
  
  import {
    normalizeMiniUser,
    patchUsersSettingsRoleCache,
    prefetchUsersSettingsRole,
    prewarmUsersSettingsRoleAvatars,
    readAllUsersSettingsRoleCaches,
    readUsersSettingsRoleCache,
    removeUserFromRoleCache,
    subscribeUsersSettingsRoleCache,
    upsertUserIntoRoleCache,
    USERS_SETTINGS_ROLE_KEYS,
  } from "../utils/usersSettingsCache";
  
  
  let socketConsumerCount =
    0;
  
  let sharedSocket =
    null;
  
  const activeRoleCounts =
    new Map();
  
  
  const sameId =
    (
      first,
      second
    ) =>
      first !==
        null &&
      first !==
        undefined &&
      second !==
        null &&
      second !==
        undefined &&
      String(first) ===
        String(second);
  
  
  const subscribeSocket =
    () => {
      sharedSocket?.emit(
        "subscribe:users"
      );
    };
  
  
  const revalidateActiveRoles =
    () => {
      activeRoleCounts.forEach(
        (
          count,
          role
        ) => {
          if (count <= 0) {
            return;
          }
  
          prefetchUsersSettingsRole(
            role,
            {
              force: true,
            }
          ).catch(
            (
              error
            ) => {
              console.warn(
                "[useUsersSettingsData] reconnect refresh failed:",
                error
              );
            }
          );
        }
      );
    };
  
  
  const onSocketConnect =
    () => {
      subscribeSocket();
  
      revalidateActiveRoles();
    };
  
  
  const onUserCreated =
    (
      payload
    ) => {
      const user =
        normalizeMiniUser(
          payload?.user ||
          {}
        );
  
      if (
        !user.id ||
        !USERS_SETTINGS_ROLE_KEYS.includes(
          user.role
        )
      ) {
        return;
      }
  
      upsertUserIntoRoleCache(
        user.role,
        user
      );
    };
  
  
  const onUserUpdated =
    (
      payload
    ) => {
      const rawUser =
        payload?.user ||
        payload?.full ||
        {};
  
      const user =
        normalizeMiniUser(
          rawUser
        );
  
      if (!user.id) {
        return;
      }
  
      const caches =
        readAllUsersSettingsRoleCaches();
  
      /*
       * If role is present, remove this user from every old role cache.
       */
      if (
        USERS_SETTINGS_ROLE_KEYS.includes(
          user.role
        )
      ) {
        USERS_SETTINGS_ROLE_KEYS.forEach(
          (
            cachedRole
          ) => {
            if (
              cachedRole ===
              user.role
            ) {
              return;
            }
  
            const cached =
              caches[
                cachedRole
              ];
  
            if (
              cached?.users?.some(
                (
                  current
                ) =>
                  sameId(
                    current.id ??
                      current.user_id,
                    user.id
                  )
              )
            ) {
              removeUserFromRoleCache(
                cachedRole,
                user.id
              );
            }
          }
        );
  
        upsertUserIntoRoleCache(
          user.role,
          user
        );
  
        return;
      }
  
      /*
       * Defensive case:
       * backend event has no role.
       *
       * Update whichever cached role currently contains the user.
       */
      USERS_SETTINGS_ROLE_KEYS.forEach(
        (
          cachedRole
        ) => {
          const cached =
            caches[
              cachedRole
            ];
  
          if (
            !cached?.users?.some(
              (
                current
              ) =>
                sameId(
                  current.id ??
                    current.user_id,
                  user.id
                )
            )
          ) {
            return;
          }
  
          upsertUserIntoRoleCache(
            cachedRole,
            user
          );
        }
      );
    };
  
  
  const onUserDeleted =
    (
      payload
    ) => {
      const id =
        payload?.user?.id ??
        payload?.user
          ?.user_id ??
        payload?.full
          ?.user_id ??
        payload?.full?.id ??
        payload?.id ??
        null;
  
      if (!id) {
        return;
      }
  
      USERS_SETTINGS_ROLE_KEYS.forEach(
        (
          role
        ) => {
          removeUserFromRoleCache(
            role,
            id
          );
        }
      );
    };
  
  
  const startSocket =
    () => {
      if (
        sharedSocket
      ) {
        return;
      }
  
      sharedSocket =
        getSocket();
  
      sharedSocket.on(
        "connect",
        onSocketConnect
      );
  
      sharedSocket.on(
        "reconnect",
        onSocketConnect
      );
  
      sharedSocket.on(
        "user:created",
        onUserCreated
      );
  
      sharedSocket.on(
        "user:updated",
        onUserUpdated
      );
  
      sharedSocket.on(
        "user:deleted",
        onUserDeleted
      );
  
      subscribeSocket();
    };
  
  
  const stopSocket =
    () => {
      if (
        !sharedSocket
      ) {
        return;
      }
  
      sharedSocket.emit(
        "unsubscribe:users"
      );
  
      sharedSocket.off(
        "connect",
        onSocketConnect
      );
  
      sharedSocket.off(
        "reconnect",
        onSocketConnect
      );
  
      sharedSocket.off(
        "user:created",
        onUserCreated
      );
  
      sharedSocket.off(
        "user:updated",
        onUserUpdated
      );
  
      sharedSocket.off(
        "user:deleted",
        onUserDeleted
      );
  
      sharedSocket =
        null;
    };
  
  
  const registerSocketConsumer =
    (
      role
    ) => {
      socketConsumerCount +=
        1;
  
      activeRoleCounts.set(
        role,
        (
          activeRoleCounts.get(
            role
          ) ||
          0
        ) +
          1
      );
  
      startSocket();
  
      return () => {
        socketConsumerCount =
          Math.max(
            0,
            socketConsumerCount -
              1
          );
  
        const nextCount =
          Math.max(
            0,
            (
              activeRoleCounts.get(
                role
              ) ||
              1
            ) -
              1
          );
  
        if (nextCount === 0) {
          activeRoleCounts.delete(
            role
          );
        } else {
          activeRoleCounts.set(
            role,
            nextCount
          );
        }
  
        if (
          socketConsumerCount ===
          0
        ) {
          stopSocket();
        }
      };
    };
  
  
  export function useUsersSettingsData(
    role
  ) {
    const initialCache =
      readUsersSettingsRoleCache(
        role
      );
  
    const [
      state,
      setState,
    ] =
      useState(() => ({
        role,
  
        data:
          initialCache,
  
        loading:
          !initialCache,
  
        error:
          "",
      }));
  
  
    /*
     * Avoid showing previous-role users for one render
     * when the user changes Admin -> Manager etc.
     */
    const currentData =
      state.role ===
      role
        ? state.data
        : readUsersSettingsRoleCache(
            role
          );
  
  
    const currentLoading =
      state.role ===
      role
        ? state.loading
        : !currentData;
  
  
    const currentError =
      state.role ===
      role
        ? state.error
        : "";
  
  
    useEffect(() => {
      let alive = true;
  
      const cached =
        readUsersSettingsRoleCache(
          role
        );
  
      setState({
        role,
  
        data:
          cached,
  
        loading:
          !cached,
  
        error:
          "",
      });
  
  
      const unsubscribe =
        subscribeUsersSettingsRoleCache(
          role,
          (
            nextData
          ) => {
            if (!alive) {
              return;
            }
  
            setState({
              role,
  
              data:
                nextData,
  
              loading:
                false,
  
              error:
                "",
            });
  
            prewarmUsersSettingsRoleAvatars(
              role
            ).catch(
              () => {}
            );
          }
        );
  
  
      prefetchUsersSettingsRole(
        role
      )
        .then(
          (
            nextData
          ) => {
            if (
              !alive ||
              !nextData
            ) {
              return;
            }
  
            setState({
              role,
  
              data:
                nextData,
  
              loading:
                false,
  
              error:
                "",
            });
  
            prewarmUsersSettingsRoleAvatars(
              role
            ).catch(
              () => {}
            );
          }
        )
        .catch(
          (
            error
          ) => {
            if (!alive) {
              return;
            }
  
            console.error(
              "[useUsersSettingsData] load failed:",
              error
            );
  
            setState(
              (
                previous
              ) => ({
                ...previous,
  
                role,
  
                loading:
                  false,
  
                error:
                  cached
                    ? ""
                    : "load_failed",
              })
            );
          }
        );
  
  
      return () => {
        alive = false;
  
        unsubscribe();
      };
    }, [
      role,
    ]);
  
  
    /*
     * One global socket subscription.
     */
    useEffect(
      () =>
        registerSocketConsumer(
          role
        ),
      [
        role,
      ]
    );
  
  
    /*
     * Smart background warming:
     *
     * Once Users Settings is open, quietly fetch the other role tabs.
     * After that Admin -> Manager -> Dispatcher is effectively instant.
     */
    useEffect(() => {
      const warmOtherRoles =
        () => {
          USERS_SETTINGS_ROLE_KEYS
            .filter(
              (
                otherRole
              ) =>
                otherRole !==
                role
            )
            .forEach(
              (
                otherRole
              ) => {
                prefetchUsersSettingsRole(
                  otherRole
                ).catch(
                  () => {
                    // background only
                  }
                );
              }
            );
        };
  
  
      if (
        typeof window.requestIdleCallback ===
        "function"
      ) {
        const idleId =
          window.requestIdleCallback(
            warmOtherRoles,
            {
              timeout: 1200,
            }
          );
  
        return () => {
          window.cancelIdleCallback?.(
            idleId
          );
        };
      }
  
  
      const timer =
        window.setTimeout(
          warmOtherRoles,
          250
        );
  
      return () => {
        window.clearTimeout(
          timer
        );
      };
    }, [
      role,
    ]);
  
  
    const refresh =
      useCallback(
        () =>
          prefetchUsersSettingsRole(
            role,
            {
              force: true,
            }
          ),
        [
          role,
        ]
      );
  
  
    const patchUsers =
      useCallback(
        (
          updater
        ) =>
          patchUsersSettingsRoleCache(
            role,
            updater
          ),
        [
          role,
        ]
      );
  
  
    return {
      users:
        currentData?.users ||
        [],
  
      loading:
        currentLoading,
  
      error:
        currentError,
  
      refresh,
  
      hasCachedData:
        Boolean(
          currentData
        ),
    };
  }